import React, { useState, useEffect } from 'react';
import { APIRequests } from '../../config/api';
import useAPI from '../../utils/useApi';
import { RequestInput } from '../../types';
import { PageSection, PageViewConfig, ListViewConfig } from '../config/layouts';
import { RelatedList } from './RelatedList';
import { TableProps } from './AdminTable';
import { Loading } from '../../components';
import dayjs from 'dayjs';
import {
  Button,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Column } from './AdminTable';
import { Divider, Grid } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DataType } from '../../types';

export interface RecordViewProps {
  id: string;
  getRequest: APIRequests;
  layout: PageViewConfig;
  updateRequest?: APIRequests;
}

export const RecordView = (props: RecordViewProps) => {
  const [record, setRecord] = useState<{ [name: string]: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  let recordGet = useAPI<{ [name: string]: string }>(props.getRequest);
  let recordUpdate: { [name: string]: any } | undefined = undefined;
  if (props.updateRequest) {
    recordUpdate = useAPI(props.updateRequest);
  }

  useEffect(() => {
    if (props.id) {
      const req: RequestInput = { pathParams: { id: props.id } };
      recordGet.execute(req);
    }
  }, [props.id]);

  useEffect(() => {
    if (recordGet.data) {
      setIsLoading(false);
      setRecord(recordGet.data);
    }
  }, [recordGet.data]);

  const handleChange = (field: string, value: string) => {
    if (record) {
      const data = record;
      data[field] = value;
      setRecord(data);
    }
  };

  const handleEdit = () => {
    if (recordUpdate) {
      const req: RequestInput = { body: record };
      recordUpdate.execute(req);
      window.location.reload();
    }
  };

  return (
    <>
      {isLoading && <Loading />}
      {!isLoading && record && (
        <>
          <Typography variant="h4">
            {record[props.layout.title]}
            {props.updateRequest && (
              <Button
                variant="outlined"
                sx={{ backgroundColor: 'blue', color: 'white', ml: 2 }}
                onClick={() => handleEdit()}
              >
                Save
              </Button>
            )}
          </Typography>
          {props.layout.sections.map((section: PageSection) => {
            return (
              <Grid container key={section.title} py={5}>
                <Grid item xs={12}>
                  <Typography variant="h6">{section.title}</Typography>
                  <Divider />
                </Grid>
                {section.fields.map((field: Column) => {
                  return (
                    <Grid item xs={12} sm={section.layout} key={field.name}>
                      <InputLabel
                        htmlFor={props.id}
                        sx={{
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                        }}
                      >
                        <Typography
                          variant="bold"
                          color="text.secondary"
                          sx={{
                            fontSize: 16,
                          }}
                        >
                          {field.label}
                        </Typography>
                      </InputLabel>
                      {field.type === DataType.Date && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            defaultValue={
                              record[field.name]
                                ? dayjs(new Date(record[field.name]))
                                : null
                            }
                            disabled={!field.editable}
                            sx={{ width: '80%' }}
                          />
                        </LocalizationProvider>
                      )}
                      {field.type == DataType.Select && (
                        <Select
                          defaultValue={record[field.name]}
                          disabled={!field.editable}
                          onChange={(e) =>
                            handleChange(field.name, e.target.value)
                          }
                          sx={{ width: '80%' }}
                        >
                          {field.fieldOptions &&
                            field.fieldOptions.map((v: string, idx: number) => {
                              return (
                                <MenuItem key={`${v}-${idx}`} value={v}>
                                  {v}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      )}
                      {(!field.type || field.type === DataType.LongText) && (
                        <TextField
                          onChange={(e) =>
                            handleChange(field.name, e.target.value)
                          }
                          maxRows={3}
                          sx={{
                            width: '80%',
                            '& .MuiInputBase-root.Mui-disabled': {
                              '& > fieldset': {
                                border: !field.editable ? '0' : 'inherit',
                              },
                            },
                          }}
                          defaultValue={record[field.name]}
                          disabled={!field.editable}
                        />
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            );
          })}
          <Typography variant="h4">Related Lists</Typography>
          {props.layout.related.map((relObj: ListViewConfig) => {
            const tableProps: Partial<TableProps> = {
              columns: relObj.columns,
            };
            return (
              <RelatedList
                key={relObj.title}
                title={relObj.title}
                apiRequest={relObj.apiRequest}
                tableProps={tableProps}
                recordid={props.id}
                relatedFilter={relObj.relatedFilter}
              />
            );
          })}
        </>
      )}
    </>
  );
};
